// src/components/Layout.jsx
import React, { useState, useRef, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import Navbar from './Navbar';
import AudioPlayer from './AudioPlayer';
import FloatingMiniPlayer from './FloatingMiniPlayer';
import { PlayerContext } from '../context/PlayerContext';
import './Layout.css';

const Layout = () => {
  const [selectedSong,    setSelectedSong]    = useState(null);
  const [currentPlaylist, setCurrentPlaylist] = useState([]);
  const [showPlayer,      setShowPlayer]      = useState(false);
  const [showMiniPlayer,  setShowMiniPlayer]  = useState(false);
  // isPlaying is the single source of truth — AudioPlayer updates it via onPlayStateChange
  const [isPlaying,       setIsPlaying]       = useState(false);

  const actionsRef = useRef({ play: () => {}, pause: () => {}, next: () => {}, previous: () => {} });

  const playSong = useCallback((song, playlist = []) => {
    setCurrentPlaylist(playlist);
    setSelectedSong(song);
    setShowPlayer(true);
    setShowMiniPlayer(false);
    setIsPlaying(true);
  }, []);

  const registerActions = useCallback((a) => { actionsRef.current = a; }, []);

  const handleNext     = (s) => { setSelectedSong(s); setIsPlaying(true); };
  const handlePrevious = (s) => { setSelectedSong(s); setIsPlaying(true); };

  const closePlayer = () => {
    actionsRef.current.pause();
    setIsPlaying(false);
    setShowPlayer(false);
    setShowMiniPlayer(false);
    setSelectedSong(null);
    setCurrentPlaylist([]);
  };

  // KEY FIX: minimize — don't change isPlaying, AudioPlayer is still running hidden
  const minimizePlayer = () => {
    setShowPlayer(false);
    setShowMiniPlayer(true);
    // isPlaying stays whatever it currently is — don't reset it
  };

  const closeMiniPlayer = () => {
    actionsRef.current.pause();
    setIsPlaying(false);
    setShowMiniPlayer(false);
    setSelectedSong(null);
    setCurrentPlaylist([]);
  };

  const expandMiniPlayer = () => {
    setShowMiniPlayer(false);
    setShowPlayer(true);
  };

  // Mini player play/pause — toggle and call actual player
  const togglePlayPause = () => {
    if (isPlaying) {
      actionsRef.current.pause();
      setIsPlaying(false);
    } else {
      actionsRef.current.play();
      setIsPlaying(true);
    }
  };

  // onPlayStateChange — AudioPlayer calls this on every real YT state change
  // This keeps Layout's isPlaying perfectly in sync with actual player state
  const handlePlayStateChange = (playing) => {
    setIsPlaying(playing);
  };

  return (
    <PlayerContext.Provider value={{ playSong, isPlaying, selectedSong }}>
      <div className="layout">
        <Navbar />

        <div className="layout__content">
          <Outlet />
        </div>

        {/* AudioPlayer — always mounted when song selected, hidden via display:none when mini */}
        {selectedSong && (
          <div style={{ display: showPlayer ? 'block' : 'none' }}>
            <AudioPlayer
              currentSong={selectedSong}
              songList={currentPlaylist}
              onNext={handleNext}
              onPrevious={handlePrevious}
              onClose={closePlayer}
              onMinimize={minimizePlayer}
              registerActions={registerActions}
              onPlayStateChange={handlePlayStateChange}  // syncs isPlaying to real YT state
            />
          </div>
        )}

        {/* Mini player — shows on ALL pages, uses same isPlaying from Layout state */}
        {showMiniPlayer && selectedSong && (
          <FloatingMiniPlayer
            currentSong={selectedSong}
            isPlaying={isPlaying}            // directly from Layout state, always in sync
            onPlayPause={togglePlayPause}
            onNext={() => actionsRef.current.next()}
            onPrevious={() => actionsRef.current.previous()}
            onClose={closeMiniPlayer}
            onExpand={expandMiniPlayer}
          />
        )}
      </div>
    </PlayerContext.Provider>
  );
};

export default Layout;